import { Download } from "@mui/icons-material";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { presentar, separarLlamada, tieneContenidoDetallable, tokenizar } from "../../../../utils/auditUtils";
import snackbarUtils from "../../../../utils/snackbarUtils";
import CustomDialog from "../../../commons/dialog/customDialog";
import CircularLoading from "../../../commons/loadingData/circularLoading";

const LIMITE_LINEAS = 1000;

const COLOR_TOKEN = {
  clave: "#1565c0",
  string: "#c62828",
  numero: "#2e7d32",
  literal: "#6a1b9a",
  signo: "#212121",
  etiqueta: "#1565c0",
  comentario: "#757575",
  texto: "#212121",
};

const CAMPOS = [
  { clave: "fecha", titulo: "Fecha y hora" },
  { clave: "prestador", titulo: "Prestador" },
  { clave: "usuario", titulo: "Usuario" },
  { clave: "email", titulo: "Email" },
  { clave: "ip", titulo: "IP" },
  { clave: "modulo", titulo: "Módulo" },
  { clave: "accion", titulo: "Acción" },
  { clave: "siniestro", titulo: "Siniestro" },
  { clave: "numeroDocumento", titulo: "DNI" },
];

const textoPlano = {
  margin: 0,
  fontFamily: "monospace",
  fontSize: 13,
  lineHeight: 1.6,
  whiteSpace: "pre",
};

const AuditoriaPrestadoresDetalleDialog = ({ open, onClose, fila, detalle, loading, onDownload }) => {
  const [lado, setLado] = useState("output");
  const [formato, setFormato] = useState("json");
  const [lineasVisibles, setLineasVisibles] = useState(LIMITE_LINEAS);
  const origen = fila || {};
  const esLogin = origen.origen === "login";
  const mostrarPanelDetalle = !loading && tieneContenidoDetallable(detalle, esLogin);
  const { llamada, cuerpo } = separarLlamada(detalle?.requestInput);
  const parametros = (cuerpo || llamada || "").trim();
  const resultado = (detalle?.requestOutput || "").trim();
  const hayParametros = !esLogin && parametros.length > 0;
  const hayResultado = !esLogin && resultado.length > 0;
  const ladoVisible = (lado === "input" && hayParametros) || (lado === "output" && hayResultado)
    ? lado
    : hayResultado
      ? "output"
      : "input";
  const crudo = esLogin
    ? (detalle?.requestOutput || detalle?.requestInput)
    : ladoVisible === "output"
      ? detalle?.requestOutput
      : (cuerpo || llamada);
  const vista = loading ? { texto: "", aviso: "", tipo: "vacio" } : presentar(crudo, formato);
  const lineasCompletas = vista.texto ? vista.texto.split("\n") : [];
  const truncado = lineasCompletas.length > lineasVisibles;
  const textoVisible = truncado ? lineasCompletas.slice(0, lineasVisibles).join("\n") : vista.texto;
  const tokens = tokenizar(textoVisible, vista.tipo);
  const lineas = textoVisible ? textoVisible.split("\n") : [];

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormato("json");
    setLado("output");
    setLineasVisibles(LIMITE_LINEAS);
  }, [open, detalle?.idMovimientoLog]);

  const campos = {
    fecha: origen.fechaHora ? moment(origen.fechaHora).format("DD/MM/YYYY HH:mm:ss") : "—",
    prestador: origen.prestador || "—",
    usuario: origen.usuario || "—",
    email: origen.email || "—",
    ip: origen.ip || "—",
    modulo: origen.modulo || "—",
    accion: origen.accion || detalle?.accion || "—",
    siniestro: origen.siniestro || detalle?.siniestro || "—",
    numeroDocumento: origen.numeroDocumento || detalle?.numeroDocumento || "—",
  };

  const handleDownload = () => {
    if (!vista.texto.trim()) {
      snackbarUtils.error("No hay datos para descargar.");
      return;
    }

    onDownload({ contenido: vista.texto, extension: formato, lado: ladoVisible });
  };

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth={mostrarPanelDetalle ? "lg" : "sm"} fullWidth dismissible>
      <DialogTitle>Detalle del Movimiento</DialogTitle>
      <DialogContent sx={{ display: "flex", p: 0, height: mostrarPanelDetalle ? 560 : "auto", overflow: "hidden" }}>
        <Box sx={{
          width: mostrarPanelDetalle ? 280 : "100%",
          flexShrink: 0,
          overflow: "auto",
          borderRight: mostrarPanelDetalle ? "1px solid #e0e0e0" : "none",
          px: 2.5,
          py: 2,
        }}>
          {loading ? <CircularLoading /> : CAMPOS.map((campo) => (
            <Box key={campo.clave} sx={{ mb: 1.75 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {campo.titulo}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {campos[campo.clave]}
              </Typography>
            </Box>
          ))}
        </Box>
        {mostrarPanelDetalle ? (
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, borderBottom: "1px solid #e0e0e0" }}>
            {hayParametros || hayResultado ? (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={ladoVisible}
                sx={(theme) => ({
                  padding: theme.spacing(0.5),
                  borderRadius: 999,
                  backgroundColor: theme.palette.action.hover,
                  "& .MuiToggleButtonGroup-grouped": {
                    margin: theme.spacing(0, 0.25),
                    border: 0,
                    borderRadius: "999px !important",
                    padding: theme.spacing(0.25, 1.75),
                    textTransform: "none",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                })}
                onChange={(_, valor) => {
                  if (valor) {
                    setLado(valor);
                    setLineasVisibles(LIMITE_LINEAS);
                  }
                }}>
                {hayParametros ? <ToggleButton value="input">Parametros</ToggleButton> : null}
                {hayResultado ? <ToggleButton value="output">Resultado</ToggleButton> : null}
              </ToggleButtonGroup>
            ) : null}
            <Tabs value={formato} onChange={(_, valor) => { setFormato(valor); setLineasVisibles(LIMITE_LINEAS); }}>
              <Tab label="JSON" value="json" />
              <Tab label="XML" value="xml" />
            </Tabs>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={handleDownload}
              color="primary"
              variant="outlined"
              startIcon={<Download />}
              disabled={loading || !detalle || !vista.texto.trim()}>
              Descargar {formato}
            </Button>
          </Box>
          {lado === "input" && llamada && cuerpo ? (
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1 }}>
              {llamada}
            </Typography>
          ) : null}
          {vista.aviso ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1 }}>
              {vista.aviso}
            </Typography>
          ) : null}
          <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", px: 2, py: 1 }}>
            {loading ? <CircularLoading /> : (
              <>
                <Box sx={{ display: "flex" }}>
                  <Box component="pre" sx={{ ...textoPlano, pr: 2, color: "#9e9e9e", textAlign: "right", userSelect: "none" }}>
                    {lineas.map((_, indice) => `${indice + 1}\n`)}
                  </Box>
                  <Box component="pre" sx={{ ...textoPlano, flex: 1 }}>
                    {tokens.map((token, indice) => (
                      <span key={indice} style={{ color: COLOR_TOKEN[token.tipo] || COLOR_TOKEN.texto }}>
                        {token.texto}
                      </span>
                    ))}
                  </Box>
                </Box>
                {truncado ? (
                  <Typography variant="body2" color="warning.main" sx={{ pt: 1 }}>
                    Actualmente se muestran {lineasVisibles} líneas. Por favor presione{" "}
                    <Button
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 0, verticalAlign: "baseline", textTransform: "none" }}
                      onClick={() => setLineasVisibles((cantidad) => cantidad + LIMITE_LINEAS)}>
                      aquí
                    </Button>
                    {" "}si quiere ver más.
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
        </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default AuditoriaPrestadoresDetalleDialog;
